import React from 'react';
import PropTypes from 'prop-types';
import './Select.css';

/**
 * Select component following UI design specifications
 * @param {Object} props - Component props
 * @returns {React.ReactElement} Select component
 */
const Select = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = '请选择...',
  error,
  helperText,
  disabled = false,
  required = false,
  className = '',
  size = 'medium',
  variant = 'default',
  ...props
}) => {
  // Base styles
  const baseStyles = `
    w-full transition-all duration-200 focus:outline-none 
    disabled:bg-gray-50 disabled:cursor-not-allowed appearance-none
  `;

  // Size styles
  const sizeStyles = {
    small: 'px-3 py-2 text-sm',
    medium: 'px-4 py-3 text-sm',
    large: 'px-4 py-4 text-base'
  };

  // Variant styles
  const variantStyles = {
    default: `
      border border-gray-300 bg-white text-gray-900
      focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
      hover:border-emerald-300
    `,
    filled: `
      border-0 bg-gray-100 text-gray-900
      focus:ring-2 focus:ring-emerald-500 focus:bg-white
      hover:bg-emerald-50
    `,
    outlined: `
      border-2 border-gray-200 bg-transparent text-gray-900
      focus:ring-0 focus:border-emerald-500
      hover:border-emerald-300
    `
  };

  // Error styles
  const errorStyles = error ? `
    border-red-300 bg-red-50 text-red-900
    focus:ring-red-500 focus:border-red-500
  ` : '';

  // Border radius
  const borderRadius = 'rounded-xl';

  // Combine select classes
  const selectClasses = [
    baseStyles,
    sizeStyles[size],
    variantStyles[variant],
    borderRadius,
    errorStyles,
    'pr-10', // Space for dropdown arrow
    'select-emerald', // Custom class for emerald styling
    className
  ].join(' ').replace(/\s+/g, ' ').trim();

  return (
    <div className="w-full">
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Select container */}
      <div className="relative">
        {/* Select field */}
        <select
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
          className={selectClasses}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option, index) => (
            <option 
              key={option.value || index} 
              value={option.value}
              disabled={option.disabled}
              style={{
                backgroundColor: value === option.value ? '#10b981' : '#ffffff',
                color: value === option.value ? '#ffffff' : '#111827'
              }}
            >
              {option.label}
            </option>
          ))}
        </select>

        {/* Dropdown arrow */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Helper text or error message */}
      {(error || helperText) && (
        <p className={`mt-2 text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
          {error || helperText}
        </p>
      )}
    </div>
  );
};

Select.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool
  })).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  className: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  variant: PropTypes.oneOf(['default', 'filled', 'outlined'])
};

export default Select; 